import { h, defineComponent, ref, watch } from 'vue';
import { n, t } from '@/ui/i18n/i18n';
import { box, column, level, loader, pagination, sortableNumericColumn } from '@/ui/components';
import { groupedIssueList, groupedIssueListExportUrl, SupportedExportFormat } from '@/services/api/resources/analytics';
import { AnalyticsEntityGroup, emptyGroupedIssueList, GroupedIssue, GroupedIssueList } from '@/entities/analytics';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import * as qs from '@/ui/queryState';
import { download, enumToSelectOption } from '@/ui/helpers/uiHelper';
import { hasFeature, hasPermission, Permissions, user } from '@/ui/auth/auth';
import { IssueGroupBy } from '@/services/api/resources/analytics/types';
import { AnalyticsTable } from '@/ui/components/table/AnalyticsTable';
import {
  assigneeFilter,
  categoryFilter,
  checklistFilter,
  creatorFilter,
  dateRangeFilter,
  filterContainer,
  groupFilter,
  issuePriorityTagFilter,
  issueStatusFilter,
  issueTypeFilter,
  questionTitleFilter,
  resolverFilter,
  responseFilter,
  sectionFilter,
  sensorFilter,
  entityItemFilter,
  entityFolderFilter,
} from '@/ui/components/filters';
import { dropdown } from '@/ui/components/controls/Dropdown';
import { SortDirection } from '@/ui/types';
import { issuePriority, issueType } from '@/ui/dictionary';
import { IssuePriority, IssueType } from '@/entities/issue';
import { exportDropdown } from '@/ui/components/dropdown/ExportDropdown';
import { renderForbiddenPage } from '../../error/ForbiddenPage';
import { decodeGroup as decodeAnalyticsGroup, mergeEntityItems } from '../codec';
import { issueGroupBy } from '../dictionary';
import { useQueryFiltering } from './useQueryFilters';
import { useFilters } from './useFilters';
import { issuesDetailPageLink } from './IssuesDetailPage';
import { renderAnalyticsTeaserPage } from '../../teasers/AnalyticsTeaserPage';
import { IssuesPageCharts } from '@/ui/pages/analytics/Issues/IssuesPageCharts';

/**
 * This is used to display the Type and Priority grouped issues where the title is
 * meaningless and we look up the localized title based on the id
 */
const decodeGroup = (groupBy: IssueGroupBy, group: AnalyticsEntityGroup) => {
  if (groupBy === IssueGroupBy.Type) {
    return issueType(group.id as IssueType);
  }
  if (groupBy === IssueGroupBy.Priority) {
    return issuePriority(parseInt(group.id as string, 10) as IssuePriority);
  }
  return decodeAnalyticsGroup(group);
};

export const IssuesPage = defineComponent({
  name: 'IssuesPage',
  setup(_, ctx) {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir } = useSorting('group', SortDirection.Ascending);
    const groupBy = qs.stringEnum<IssueGroupBy>(
      'groupBy',
      Object.values(IssueGroupBy),
      IssueGroupBy.Assignee,
      false,
    );
    const {
      start,
      end,
      creator,
      assignee,
      resolver,
      question,
      entityItem,
      entityFolder,
      group,
      category,
      checklist,
      section,
      inspection,
      sensor,
      type,
      priority,
      response,
      questionTitle,
      status,
      overdue,
    } = useQueryFiltering();

    // get list task
    const query = () => ({
      timezone: user.value.timezone,
      page: page.value,
      perPage: perPage.value,
      sort: sort.value,
      dir: dir.value,
      start: start.value,
      end: end.value,
      creator: creator.value,
      assignee: assignee.value,
      resolver: resolver.value,
      question: question.value,
      entityItem: mergeEntityItems(entityFolder.value, entityItem.value),
      group: group.value,
      category: category.value,
      checklist: checklist.value,
      section: section.value,
      inspection: inspection.value,
      sensor: sensor.value,
      type: type.value,
      priority: priority.value,
      response: response.value,
      questionTitle: questionTitle.value,
      status: status.value,
      overdue: overdue.value,
    });
    const list = ref<GroupedIssueList>(emptyGroupedIssueList);
    const getList = useTask(
      () => groupedIssueList(groupBy.value, query()),
      userList => {
        list.value = userList;
        initialLoading.value = false;
      },
    );
    watch([
      page,
      perPage,
      group,
      entityItem,
      entityFolder,
      assignee,
      category,
      checklist,
      section,
      inspection,
      sensor,
      type,
      priority,
      response,
      questionTitle,
      status,
      creator,
      start,
      end,
      resolver,
      groupBy,
      sort,
      dir,
    ], getList.run);

    // export task
    const { run: exportData } = useTask(
      (format: SupportedExportFormat | undefined) => groupedIssueListExportUrl(
        groupBy.value,
        query(),
        format as SupportedExportFormat,
        user.value.locale,
      ),
      download,
    );

    // get filters task
    const { getFilters, entityItems, entityFolders, groups, users, categories, checklists, sensors, sections } = useFilters();

    return function render() {
      return h('div', [
        h('div', [
          h('div', { class: 'columns legacy-wrapper' }, [
            filterContainer([
              loader('form', getFilters, [
                dateRangeFilter(start, end, resetPagination),
                entityItemFilter(entityItem, entityItems.value, resetPagination),
                entityFolderFilter(entityFolder, entityFolders.value, resetPagination),
                groupFilter(group, groups.value, resetPagination),
                creatorFilter(creator, users.value, resetPagination),
                assigneeFilter(assignee, users.value, resetPagination),
                resolverFilter(resolver, users.value, resetPagination),
                categoryFilter(category, categories.value, resetPagination),
                checklistFilter(checklist, checklists.value, resetPagination),
                sectionFilter(section, sections.value, resetPagination),
                questionTitleFilter(questionTitle, resetPagination),
                responseFilter(response, resetPagination),
                issueTypeFilter(type, resetPagination),
                sensorFilter(sensor, sensors.value, resetPagination),
                issuePriorityTagFilter(priority, resetPagination),
                issueStatusFilter(status, resetPagination),
              ]),
            ]),
            h('div', { class: 'column' }, [
              h(IssuesPageCharts, {
                class: 'legacy',
                props: {
                  query: query(),
                  issueCount: list.value.meta.issueCount,
                },
              }),
              box([
                loader('div', getList, [
                  level(
                    [
                      //pageTitle(t('common.issues')),
                      dropdown<IssueGroupBy>(
                        {
                          value: groupBy.value,
                          options: Object
                            .values(IssueGroupBy)
                            .map(enumToSelectOption(issueGroupBy)),
                          disabled: false,
                        },
                        (value: IssueGroupBy) => qs.updateMultiple(() => {
                          resetPagination();
                          groupBy.value = value;
                        }),
                      ),
                    ],
                    [exportDropdown(exportData)],
                  ),
                  h(AnalyticsTable, {
                    class: 'analytics-issues-table',
                    props: {
                      list: list.value.data,
                      sort: sort.value,
                      dir: dir.value,
                      //use client side pagination if no backend pagination
                      paginated: (list.value.data.length > 0 && !list.value.meta.total),
                      perPage: perPage.value,
                      backendPagination: !!list.value.meta.total,
                    },
                    scopedSlots: {
                      default: () => [
                        column({
                          // this customKey property is needed because BTable uses the label
                          // as the primary identifier when refreshing columns, and when column
                          // labels change, it keeps adding new columns
                          customKey: 'group',
                          field: 'group',
                          label: issueGroupBy(groupBy.value),
                          sortable: true,
                        }, {
                          default: ({ row }: { row: GroupedIssue }) => [
                            issuesDetailPageLink(
                              decodeGroup(groupBy.value, row.group),
                              groupBy.value,
                              row.group,
                              {
                                start: start.value,
                                end: end.value,
                                creator: creator.value,
                                assignee: assignee.value,
                                resolver: resolver.value,
                                question: question.value,
                                entityItem: entityItem.value,
                                group: group.value,
                                category: category.value,
                                checklist: checklist.value,
                                section: section.value,
                                inspection: inspection.value,
                                sensor: sensor.value,
                                type: type.value,
                                priority: priority.value,
                                response: response.value,
                                questionTitle: questionTitle.value,
                                status: status.value,
                              },
                            ),
                          ],
                        }),
                        sortableNumericColumn({
                          field: 'overdueIssueCount',
                          label: t('issues.overdue'),
                        }, {
                          default: ({ row }: { row: GroupedIssue }) =>
                            n(row.overdueIssueCount, 'analyticsInteger'),
                        }),
                        sortableNumericColumn({
                          field: 'openIssueCount',
                          label: t('issues.open'),
                        }, {
                          default: ({ row }: { row: GroupedIssue }) =>
                            n(row.openIssueCount, 'analyticsInteger'),
                        }),
                        sortableNumericColumn({
                          field: 'openIssuePercentage',
                          label: t('issues.open-ratio'),
                        }, {
                          default: ({ row }: { row: GroupedIssue }) =>
                            n(row.openIssuePercentage, 'analyticsPercentage'),
                        }),
                        sortableNumericColumn({
                          field: 'issueCount',
                          label: t('issues.count'),
                        }, {
                          default: ({ row }: { row: GroupedIssue }) =>
                            n(row.issueCount, 'analyticsInteger'),
                        }),
                      ],
                    },
                    on: {
                      sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                        resetPagination();
                        sort.value = field;
                        dir.value = direction;
                      }),
                    },
                  }),
                  (list.value.meta.total)
                    ? pagination({
                      current: page.value,
                      total: list.value.meta.total,
                      perPage: perPage.value,
                    })
                    : null,
                ], !initialLoading.value),
              ]),
            ]),
          ]),
        ]),
      ]);
    };
  },
});
